import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  onboardingCompleted: boolean | null;
  checkOnboardingStatus: () => Promise<boolean>;
  completeOnboarding: () => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<{ user: User | null; session: Session | null }>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);

  const checkOnboardingStatus = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('onboarding_completed')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      const completed = data?.onboarding_completed === true;
      setOnboardingCompleted(completed);
      return completed;
    } catch (error) {
      console.error('Erro ao verificar onboarding:', error);
      return false;
    }
  }, [user]);

  const completeOnboarding = async () => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('id', user.id);

      if (error) throw error;
      setOnboardingCompleted(true);
    } catch (error) {
      console.error('Erro ao completar onboarding:', error);
    }
  };

  useEffect(() => {
    let mounted = true;

    // Verificar sessão atual com tratamento de erro de rede
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!mounted) return;
      if (error) {
        // Erro de rede ao buscar sessão — não travar o app
        console.warn('Aviso: não foi possível verificar a sessão (sem conexão?):', error.message);
      }
      setSession(session ?? null);
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch((err) => {
      if (!mounted) return;
      console.warn('Erro inesperado ao obter sessão:', err);
      setLoading(false);
    });

    // Escutar mudanças de autenticação
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      if (!mounted) return;

      // Ignorar eventos de erro de rede silenciosamente
      if (
        event === 'TOKEN_REFRESHED' ||
        event === 'SIGNED_IN' ||
        event === 'SIGNED_OUT' ||
        event === 'USER_UPDATED' ||
        event === 'INITIAL_SESSION'
      ) {
        setSession(session ?? null);
        setUser(session?.user ?? null);
        setLoading(false);
      }

      // TOKEN_REFRESH_FAILED: sessão expirou ou sem rede — limpar estado sem travar
      if (event === 'TOKEN_REFRESH_FAILED' as AuthChangeEvent) {
        console.warn('Token refresh falhou (sem rede ou sessão expirada). Usuário será deslogado.');
        setSession(null);
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Verificar status do onboarding quando o usuário mudar
  useEffect(() => {
    if (user) {
      checkOnboardingStatus();
    } else {
      setOnboardingCompleted(null);
    }
  }, [user, checkOnboardingStatus]);

  const signUp = async (email: string, password: string, name: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
        },
        emailRedirectTo: `${window.location.origin}/onboarding`,
      },
    });

    if (error) throw error;

    // Criar perfil do usuário com onboarding_completed = false
    if (data.user) {
      const { error: profileError } = await supabase
        .from('profiles')
        .insert([
          {
            id: data.user.id,
            name,
            email,
            onboarding_completed: false,
            created_at: new Date().toISOString(),
          },
        ]);

      if (profileError && profileError.code !== '23505') {
        console.error('Erro ao criar perfil:', profileError);
      }
    }

    return { user: data.user, session: data.session };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });

    if (error) throw error;
  };

  const value = {
    user,
    session,
    loading,
    onboardingCompleted,
    checkOnboardingStatus,
    completeOnboarding,
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}
